import { useState, useCallback, useMemo } from 'react';
import { Dialog } from '@repo/atomic-components';
import { useGetToolkitQuery, type ToolkitTool } from '@/api/toolkit';
import {
  makeSelectCollectionMatchCounts,
  makeSelectFilteredToolkitGroups,
  selectToolkitGroupCollections,
  useAppSelector,
} from '@/store';
import { normalizeToolkitSearchQuery } from '@/utils/toolkitSearch';
import { ToolkitPanel } from './components/ToolkitPanel';
import { ToolkitModalContent } from './components/ToolkitModalContent';

interface ToolkitModalProps {
  visible: boolean;
  onHide: () => void;
  onToolSelect: (tool: ToolkitTool) => void;
  isModal: boolean;
}

export const ToolkitModal = ({ visible, onHide, onToolSelect, isModal }: ToolkitModalProps) => {
  const [searchQuery, setSearchQuery] = useState('');

  const { isLoading } = useGetToolkitQuery({});

  const collections = useAppSelector(selectToolkitGroupCollections);
  const [selectedCollectionTitle, setSelectedCollectionTitle] = useState<string | null>(null);

  const selectCollectionMatchCounts = useMemo(() => makeSelectCollectionMatchCounts(), []);
  const collectionMatchCounts = useAppSelector((state) => selectCollectionMatchCounts(state, searchQuery));

  const hasSearchQuery = !!normalizeToolkitSearchQuery(searchQuery);

  const visibleCollections = useMemo(() => {
    if (!hasSearchQuery) {
      return collections;
    }

    return collections.filter((collection) => (collectionMatchCounts[collection.collectionTitle] ?? 0) > 0);
  }, [collections, collectionMatchCounts, hasSearchQuery]);

  // Falls back to the first visible collection; while searching, only collections with matches are visible.
  const activeCollectionTitle = useMemo<string | null>(() => {
    if (visibleCollections.length === 0) {
      return null;
    }
    if (selectedCollectionTitle && visibleCollections.some((c) => c.collectionTitle === selectedCollectionTitle)) {
      return selectedCollectionTitle;
    }
    return visibleCollections[0].collectionTitle;
  }, [visibleCollections, selectedCollectionTitle]);

  const selectFilteredToolkitGroups = useMemo(() => makeSelectFilteredToolkitGroups(), []);
  const filteredGroups = useAppSelector((state) =>
    selectFilteredToolkitGroups(state, searchQuery, activeCollectionTitle),
  );

  const handleToolClick = (tool: ToolkitTool) => {
    onToolSelect(tool);
    onHideHandler();
  };

  const onHideHandler = () => {
    onHide();
    setSearchQuery('');
  };

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  if (!isModal) {
    return (
      <ToolkitPanel visible={visible} onHide={onHideHandler}>
        <ToolkitModalContent
          searchQuery={searchQuery}
          isLoading={isLoading}
          filteredGroups={filteredGroups}
          collections={visibleCollections}
          activeCollectionTitle={activeCollectionTitle}
          collectionMatchCounts={collectionMatchCounts}
          handleSearchChange={handleSearchChange}
          handleToolClick={handleToolClick}
          handleCollectionChange={setSelectedCollectionTitle}
          compact
        />
      </ToolkitPanel>
    );
  }

  return (
    <Dialog
      visible={visible}
      onHide={onHideHandler}
      modal
      draggable={false}
      closable
      header="Choose a prompt from our well-crafted toolkit"
      className="!h-[90vh] !w-[90vw]"
      pt={{
        root: {
          className: 'rounded-lg dialog-shadow !h-[90vh] !w-[90vw] flex flex-col',
        },
        header: {
          className: '!rounded-t-lg bg-theme-white text-theme-black cursor-default !px-4 lg:!pl-15 lg:!pr-6',
        },
        headerTitle: {
          className: 'w-full min-w-0 text-center text-lg leading-tight font-bold lg:text-2xl lg:leading-8',
        },
        // Fills what the header leaves, so a taller header can't overflow the dialog.
        content: {
          className: 'bg-theme-white rounded-b-lg pb-0 min-h-0 flex-1 overflow-hidden px-4 lg:px-6 flex flex-col',
        },
        closeButtonIcon: { className: 'text-theme-text-grey' },
      }}
    >
      <ToolkitModalContent
        searchQuery={searchQuery}
        isLoading={isLoading}
        filteredGroups={filteredGroups}
        collections={visibleCollections}
        activeCollectionTitle={activeCollectionTitle}
        collectionMatchCounts={collectionMatchCounts}
        handleSearchChange={handleSearchChange}
        handleToolClick={handleToolClick}
        handleCollectionChange={setSelectedCollectionTitle}
      />
    </Dialog>
  );
};
